import type { ReactNode } from 'react'
import { Button, Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { LoadingOutlined, MoreOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { Permission } from '@/shared/auth/permissions'
import { usePermissions } from '@/shared/auth/usePermissions'

export interface RowAction {
  key: string
  label: ReactNode
  onClick: () => void
  icon?: ReactNode
  /** Ruxsatlardan BIRORTASI yetsa ko'rinadi. Berilmasa — huquq talab qilinmaydi. */
  permission?: Permission | readonly Permission[]
  danger?: boolean
  disabled?: boolean
  loading?: boolean
  /**
   * Chaqiruvchi hisoblagan shart (masalan faqat `ISUP_GATEWAY` qurilmada).
   * `true` bo'lsa amal ro'yxatda umuman ko'rinmaydi.
   */
  hidden?: boolean
  /** Menyu ichida ajratuvchi chiziq — shu amaldan OLDIN qo'yiladi. */
  divider?: boolean
}

export interface RowActionsProps {
  items: readonly RowAction[]
  /**
   * ⚠️ Amallar ustuni bir necha tugma bilan kengayib ketsa (masalan
   * davomat qurilmalari — 6 ta amal) jadval ma'lumoti ko'rinmay qolardi.
   * Barcha amal bitta "uch nuqta" menyusiga yig'iladi va ustun ~48px ga
   * qisqaradi. Huquq va `hidden` shu yerda filtrlanadi — sahifa har
   * amalni alohida `<Can>` bilan o'rashi shart emas.
   */
  disabled?: boolean
}

export function RowActions({ items, disabled }: RowActionsProps) {
  const { t } = useTranslation()
  const { canAny } = usePermissions()

  const visible = items.filter(
    (a) => !a.hidden && (a.permission ? canAny(...toList(a.permission)) : true),
  )
  if (visible.length === 0) {
    return null
  }

  const menuItems: MenuProps['items'] = []
  for (const a of visible) {
    if (a.divider && menuItems.length > 0) {
      menuItems.push({ type: 'divider' })
    }
    menuItems.push({
      key: a.key,
      label: a.label,
      icon: a.loading ? <LoadingOutlined /> : a.icon,
      danger: a.danger,
      disabled: a.disabled || a.loading,
      onClick: () => a.onClick(),
    })
  }

  return (
    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
      <Tooltip title={t('actions.rowMenu')}>
        <Button
          type="text"
          size="small"
          aria-label={t('actions.rowMenu')}
          icon={<MoreOutlined style={{ fontSize: 18 }} />}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    </Dropdown>
  )
}

function toList(p: Permission | readonly Permission[]): readonly Permission[] {
  return typeof p === 'string' ? [p] : p
}
